import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '@/context/AppContext';
import { apiFetch, ApiError } from '@/lib/api';
import { buildMockupDebugSession } from '@/data/dayDiaryDebugFixture';

/** Polling interval (ms) while a session is `running` (background visions). */
const POLL_INTERVAL_MS = 1000;

// ── Wire shapes (mirror web/src/debug/day-diary/types.ts) ───────────────────

export type DebugStepKind =
  | 'fan-out'
  | 'text-pass.project'
  | 'text-pass.fallback'
  | 'vision-pass.enrich'
  | 'vision-pass.attribute'
  | 'consolidate.project'
  | 'consolidate.fallback'
  | 'done';

export type DebugSessionStatus = 'idle' | 'running' | 'done' | 'error';

export interface DebugStepPrompt {
  system: string | null;
  user: string;
  model: string;
}

export interface DebugStepResponse {
  raw: string;
  parsed: unknown;
  inputTokens: number;
  outputTokens: number;
  elapsedMs: number;
}

export interface DebugStepChild {
  kind: DebugStepKind;
  label: string;
}

/**
 * Resolved metadata for one activity referenced by a consolidate step's
 * suggestions / entries. Mirrors `ActivityLookupEntry` in
 * `web/src/debug/day-diary/types.ts`.
 */
export interface ActivityLookupEntry {
  activityName: string;
  projectName: string | null;
  clientName: string | null;
  isPersonal: boolean;
}

/**
 * Compact wire-shape summary of a Stage 2 vision verdict for one event.
 * Mirrors `VisionVerdictSummary` in `web/src/debug/day-diary/types.ts`.
 */
export interface VisionVerdictSummary {
  kind: 'enrich' | 'attribute' | 'skipped';
  verdict: string;
  reason: string | null;
  observed: string | null;
  observedText: readonly string[];
  /** Enrich mode only. */
  refinedDescription: string | null;
  /** Attribute mode only. */
  evidence: string | null;
  /** 0..1 for LLM modes, null for skipped. */
  confidence: number | null;
  /** Populated when vision was skipped due to an actual exception. */
  errorMessage: string | null;
}

/**
 * The event's current classification — where it sits in `ClassifiedEvent`
 * today, before the day-diary pipeline suggests a move. Mirrors
 * `EventCategorization` in `web/src/debug/day-diary/types.ts`.
 */
export interface EventCategorization {
  source: string;
  projectName: string | null;
  clientName: string | null;
}

/**
 * Resolved metadata for one event the summary panel needs to expose under
 * a suggestion row (app, title, url, screenshot). Mirrors the columns
 * `BatchEventRow` carries on the backend plus the `screenshotId` resolved
 * via `screenshot.insightfulScreenshotId = event.sourceNativeId`.
 */
export interface EventMeta {
  id: string;
  app: string;
  title: string | null;
  url: string | null;
  /** Null when the event has no captured screenshot (desktop-only apps). */
  screenshotId: string | null;
  occurredAt: string;
  /** Null when this event did not go through vision in the current run. */
  visionVerdict: VisionVerdictSummary | null;
  /** Null when no ClassifiedEvent row exists. */
  categorization: EventCategorization | null;
}

/**
 * Operator-facing metadata attached to consolidate steps by the runner so
 * the summary panel can render a client → project → activity tree without
 * a second round-trip. Mirrors `SummaryContext` in
 * `web/src/debug/day-diary/types.ts`.
 */
export interface SummaryContext {
  kind: 'consolidate.project' | 'consolidate.fallback';
  batchProject: { id: string; name: string; clientName: string } | null;
  activityLookup: Record<string, ActivityLookupEntry>;
  /** Resolved metadata for every event referenced by this step's suggestions / entries. */
  events: Record<string, EventMeta>;
  personalActivityExists: boolean;
}

export interface DebugStep {
  index: number;
  kind: DebugStepKind;
  label: string;
  parentIndex: number | null;
  prompt: DebugStepPrompt | null;
  response: DebugStepResponse | null;
  fanOut: DebugStepChild[];
  error: { message: string; stack?: string } | null;
  summaryContext: SummaryContext | null;
}

export interface DebugSessionDto {
  id: string;
  orgId: string;
  employeeId: string;
  date: string;
  createdAt: string;
  lastActivityAt: string;
  status: DebugSessionStatus;
  steps: DebugStep[];
}

interface AdvanceResponse {
  step: DebugStep;
  status: DebugSessionStatus;
}

interface RunVisionsResponse {
  startedCount: number;
  status: DebugSessionStatus;
}

interface RunAllResponse {
  started: boolean;
  status: DebugSessionStatus;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export interface UseDebugSessionResult {
  readonly session: DebugSessionDto | null;
  readonly isStarting: boolean;
  readonly isAdvancing: boolean;
  readonly error: string | null;
  start(input: { employeeId: string; date: string }): Promise<void>;
  next(): Promise<void>;
  /**
   * Run every pending vision step for the current batch in parallel —
   * server-side fan-out matching production. No-op when the next pending
   * step is not a vision step.
   */
  runVisions(): Promise<void>;
  /**
   * Run every remaining step in the session to completion server-side.
   * Visions within each batch run in parallel, text-pass and consolidate
   * frames serialize because consolidate depends on text-pass output.
   * Fire-and-forget — the polling loop watches steps stream in until
   * `session.status` transitions from `running` to `done`.
   */
  runAll(): Promise<void>;
  abort(): Promise<void>;
  reset(): void;
}

export function useDebugSession(): UseDebugSessionResult {
  const { currentOrg, getToken, mockupMode } = useAppContext();
  const [session, setSession] = useState<DebugSessionDto | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Polling loop: while the session is `running` (background visions in
  // flight), GET /sessions/:id every POLL_INTERVAL_MS and replace local
  // session state with the server's. Stops as soon as status leaves
  // 'running'. The latest session is held in a ref so the polling callback
  // doesn't get a stale reference between renders.
  const sessionRef = useRef<DebugSessionDto | null>(null);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (session === null || session.status !== 'running' || !currentOrg.id) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const current = sessionRef.current;
      if (current === null || current.status !== 'running') return;
      try {
        const token = await getToken();
        const fresh = await apiFetch<DebugSessionDto>(
          `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${current.id}`,
          token,
        );
        if (!cancelled) setSession(fresh);
      } catch (err) {
        // A failed poll is non-fatal — keep trying. Surface a non-blocking
        // error so the operator notices repeated failures.
        if (!cancelled) setError(errorMessage(err));
      }
    };
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [session, currentOrg.id, getToken]);

  const start = useCallback(
    async (input: { employeeId: string; date: string }) => {
      if (!currentOrg.id) return;
      setError(null);
      // Mockup mode: skip the backend, hand back a deterministic fixture
      // session so the page (and the extraction-summary panel) can be
      // previewed without Clerk auth or a live Anthropic key.
      if (mockupMode) {
        setSession(
          buildMockupDebugSession({
            orgId: currentOrg.id,
            employeeId: input.employeeId,
            date: input.date,
          }),
        );
        return;
      }
      setIsStarting(true);
      try {
        const token = await getToken();
        const created = await apiFetch<DebugSessionDto>(
          `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions`,
          token,
          { method: 'POST', body: JSON.stringify(input) },
        );
        setSession(created);
      } catch (err) {
        setError(errorMessage(err));
        setSession(null);
      } finally {
        setIsStarting(false);
      }
    },
    [currentOrg.id, getToken, mockupMode],
  );

  const next = useCallback(async () => {
    if (!session || !currentOrg.id) return;
    if (session.status === 'done') return;
    setError(null);
    setIsAdvancing(true);
    try {
      const token = await getToken();
      const advance = await apiFetch<AdvanceResponse>(
        `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${session.id}/next`,
        token,
        { method: 'POST', body: '{}' },
      );
      setSession((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              status: advance.status,
              steps: [...prev.steps, advance.step],
            },
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsAdvancing(false);
    }
  }, [session, currentOrg.id, getToken]);

  const runVisions = useCallback(async () => {
    if (!session || !currentOrg.id) return;
    if (session.status === 'done') return;
    setError(null);
    setIsAdvancing(true);
    try {
      const token = await getToken();
      // Fire-and-forget: the server starts the vision frames in the
      // background and returns immediately. Each frame pre-allocates a
      // placeholder step that the polling effect (above) will see appear
      // and then be replaced with the completed step.
      const result = await apiFetch<RunVisionsResponse>(
        `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${session.id}/run-visions`,
        token,
        { method: 'POST', body: '{}' },
      );
      // Immediately fetch fresh session state so the placeholders show up
      // without waiting a full poll interval. The polling effect takes
      // over from there until status leaves 'running'.
      const fresh = await apiFetch<DebugSessionDto>(
        `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${session.id}`,
        token,
      );
      setSession({ ...fresh, status: result.status });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsAdvancing(false);
    }
  }, [session, currentOrg.id, getToken]);

  const runAll = useCallback(async () => {
    if (!session || !currentOrg.id) return;
    if (session.status === 'done') return;
    setError(null);
    setIsAdvancing(true);
    try {
      const token = await getToken();
      // Fire-and-forget: the server kicks off a background driver loop
      // that runs every remaining frame. Each frame pre-allocates a
      // placeholder step that the polling effect will see appear and
      // then be replaced with the completed step.
      const result = await apiFetch<RunAllResponse>(
        `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${session.id}/run-all`,
        token,
        { method: 'POST', body: '{}' },
      );
      const fresh = await apiFetch<DebugSessionDto>(
        `/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${session.id}`,
        token,
      );
      setSession({ ...fresh, status: result.status });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsAdvancing(false);
    }
  }, [session, currentOrg.id, getToken]);

  const abort = useCallback(async () => {
    if (!session || !currentOrg.id) return;
    const sessionId = session.id;
    setSession(null);
    setError(null);
    // Mockup mode: fixture session has no server-side state to clean up.
    if (mockupMode) return;
    try {
      const token = await getToken();
      await apiFetch(`/api/v1/orgs/${currentOrg.id}/debug/day-diary/sessions/${sessionId}`, token, {
        method: 'DELETE',
      });
    } catch {
      // Best-effort cleanup — server-side sweep will catch it within the
      // idle timeout if the DELETE fails.
    }
  }, [session, currentOrg.id, getToken, mockupMode]);

  const reset = useCallback(() => {
    setSession(null);
    setError(null);
  }, []);

  return {
    session,
    isStarting,
    isAdvancing,
    error,
    start,
    next,
    runVisions,
    runAll,
    abort,
    reset,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}
